import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

import {
  DEFAULT_INSIGHTS_CONFIGURATION,
  getInsightsConfiguration,
  getInsightsConfigurationRecord,
} from "../../../lib/insightsFeature";
import { updateInsightsConfigurationMutation } from "../queries";

function numberFrom(value, fallback) {
  return Number(value ?? fallback);
}

export function useOverviewBudget(facility, updateFacility) {
  const record = getInsightsConfigurationRecord(facility);
  const insightsConfiguration = getInsightsConfiguration(facility) || {};
  const budgetConfiguration = insightsConfiguration.overview?.budgetTracker || {};
  const defaults = DEFAULT_INSIGHTS_CONFIGURATION.overview.budgetTracker;
  const recoveryTargets = budgetConfiguration.recoveryTargets || {};

  const configuredRevenue = numberFrom(
    budgetConfiguration.revenuePerMonth,
    defaults.revenuePerMonth,
  );
  const configuredBuzzOuts = numberFrom(
    budgetConfiguration.buzzOutsPerDay ?? recoveryTargets.buzzOutsPerDayTarget,
    defaults.buzzOutsPerDay,
  );
  const configuredFocCards = numberFrom(
    budgetConfiguration.focCardsPerDay ?? recoveryTargets.focCardsPerDayTarget,
    defaults.focCardsPerDay,
  );

  const [updateConfiguration] = useMutation(updateInsightsConfigurationMutation);

  const [revenue, setRevenue] = useState(configuredRevenue);
  const [buzzOuts, setBuzzOuts] = useState(configuredBuzzOuts);
  const [focCards, setFocCards] = useState(configuredFocCards);
  const [revenueLocked, setRevenueLocked] = useState(
    Boolean(budgetConfiguration.revenuePerMonthLocked),
  );
  const [buzzOutsLocked, setBuzzOutsLocked] = useState(
    Boolean(budgetConfiguration.buzzOutsPerDayLocked),
  );
  const [focCardsLocked, setFocCardsLocked] = useState(
    Boolean(budgetConfiguration.focCardsPerDayLocked),
  );

  useEffect(() => setRevenue(configuredRevenue), [configuredRevenue]);
  useEffect(() => setBuzzOuts(configuredBuzzOuts), [configuredBuzzOuts]);
  useEffect(() => setFocCards(configuredFocCards), [configuredFocCards]);
  useEffect(
    () => setRevenueLocked(Boolean(budgetConfiguration.revenuePerMonthLocked)),
    [budgetConfiguration.revenuePerMonthLocked],
  );
  useEffect(
    () => setBuzzOutsLocked(Boolean(budgetConfiguration.buzzOutsPerDayLocked)),
    [budgetConfiguration.buzzOutsPerDayLocked],
  );
  useEffect(
    () => setFocCardsLocked(Boolean(budgetConfiguration.focCardsPerDayLocked)),
    [budgetConfiguration.focCardsPerDayLocked],
  );

  async function saveInput(key, value, locked) {
    const patch = {
      overview: {
        budgetTracker: {
          [key]: value,
          [`${key}Locked`]: locked,
        },
      },
    };

    if (!record) {
      console.error("Insights feature configuration is not present for this facility.");
      return;
    }

    const { data } = await updateConfiguration({
      variables: { facilityId: facility.id, patch },
    });
    const saved = data?.updateInsightsConfiguration;

    if (!saved || !updateFacility) return;

    const current = facility.featureFacilityProviderConfigurations || [];
    const matchesRecord = (item) => (
      item.id === saved.id
      || item.id === record.id
      || item.feature?.name === "INSIGHTS"
    );
    const nextRecords = current.some(matchesRecord)
      ? current.map((item) => matchesRecord(item) ? { ...item, ...saved } : item)
      : [...current, { ...record, ...saved }];

    updateFacility({ featureFacilityProviderConfigurations: nextRecords });
  }

  const inputs = [
    {
      label: "Expected revenue/month",
      key: "revenuePerMonth",
      value: revenue,
      setValue: setRevenue,
      locked: revenueLocked,
      setLocked: setRevenueLocked,
      max: 10000000,
      step: 100,
      prefix: "$",
    },
    {
      label: "Buzz-outs/day",
      key: "buzzOutsPerDay",
      value: buzzOuts,
      setValue: setBuzzOuts,
      locked: buzzOutsLocked,
      setLocked: setBuzzOutsLocked,
      max: 100,
      step: 0.1,
    },
    {
      label: "FOC cards/day",
      key: "focCardsPerDay",
      value: focCards,
      setValue: setFocCards,
      locked: focCardsLocked,
      setLocked: setFocCardsLocked,
      max: 100,
      step: 0.1,
    },
  ];

  return {
    inputs,
    activeMonthlyRevenue: revenueLocked ? configuredRevenue : revenue,
    activeBuzzOutsPerDay: buzzOutsLocked ? configuredBuzzOuts : buzzOuts,
    activeFocCardsPerDay: focCardsLocked ? configuredFocCards : focCards,
    saveInput,
  };
}
